import React, { useEffect, useState } from "react";
import { Check, Link } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/** How long the button shows its copied state before resetting. */
export const COPIED_DURATION_MS = 1500;

interface CopyLinkButtonProps {
  path: string;
  label: string;
  ariaLabel: string;
  size?: "icon" | "icon-sm";
}

/**
 * Icon button that copies an in-app link as a titled link and confirms in place.
 * @param props.path - App-relative path the link points to, e.g. `/opportunities/123`
 * @param props.label - Entity name, used as the pasted link text and the toast description
 * @param props.ariaLabel - Accessible name for the icon button
 * @param props.size - Icon button size
 * @returns The copy-link button with its tooltip
 */
export const CopyLinkButton = ({
  path,
  label,
  ariaLabel,
  size = "icon",
}: CopyLinkButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  useEffect(() => {
    if (!isCopied) {
      return;
    }
    const timer = setTimeout(() => setIsCopied(false), COPIED_DURATION_MS);
    return () => clearTimeout(timer);
  }, [isCopied]);

  const handleCopy = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const url = `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.write([toLinkClipboardItem(url, label)]);
      setIsCopied(true);
      toast.success("Link copied", { description: label });
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  return (
    <TooltipProvider>
      <Tooltip open={isTooltipOpen || isCopied} onOpenChange={setIsTooltipOpen}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size={size}
            aria-label={ariaLabel}
            className="shrink-0 text-muted-foreground hover:text-foreground"
            onClick={handleCopy}
          >
            {isCopied ? (
              <Check className="text-success motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:fade-in-0" />
            ) : (
              <Link />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{isCopied ? "Copied" : "Copy link"}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Builds a clipboard item that pastes as a titled link in rich editors and as the raw URL elsewhere.
 * @param url - Absolute URL to copy
 * @param label - Link text for the rich-text variant
 * @returns The clipboard item with `text/html` and `text/plain` representations
 */
const toLinkClipboardItem = (url: string, label: string) =>
  new ClipboardItem({
    "text/html": new Blob(
      [`<a href="${escapeHtml(url)}">${escapeHtml(label)}</a>`],
      { type: "text/html" },
    ),
    "text/plain": new Blob([url], { type: "text/plain" }),
  });

/**
 * Escapes text for safe interpolation into HTML content or attribute values.
 * @param text - Raw text
 * @returns The HTML-escaped text
 */
const escapeHtml = (text: string) =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
